/**
 * Hook to detect new app versions and automatically reload
 *
 * Periodically checks if a new version has been deployed by fetching
 * the index.html and comparing the build time meta tag.
 * Automatically reloads the page when a new version is detected.
 */

import { useEffect, useRef } from 'react'

const CHECK_INTERVAL = 5 * 60 * 1000 // Check every 5 minutes
const INITIAL_BUILD_TIME_KEY = 'app_initial_build_time'

/**
 * Get build time from the current page's meta tag
 */
const getCurrentBuildTime = (): string | null => {
  const metaTag = document.querySelector('meta[name="build-time"]')
  return metaTag?.getAttribute('content') || null
}

/**
 * Fetch the index.html and extract build time from meta tag
 */
const fetchLatestBuildTime = async (): Promise<string | null> => {
  try {
    const response = await fetch('/index.html', {
      method: 'GET',
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })

    if (!response.ok) {
      return null
    }

    const html = await response.text()
    const match = html.match(/<meta name="build-time" content="([^"]+)"/)
    return match ? match[1] : null
  } catch (error) {
    console.warn('Failed to check for new version:', error)
    return null
  }
}

/**
 * Check if a new version is available and reload if needed
 */
const checkForNewVersion = async (initialBuildTime: string) => {
  const latestBuildTime = await fetchLatestBuildTime()

  if (!latestBuildTime) {
    return // Failed to fetch, skip this check
  }

  // If build time has changed, a new version is available
  if (latestBuildTime !== initialBuildTime && latestBuildTime !== 'BUILD_TIME_PLACEHOLDER') {
    console.log('New version detected, reloading...')
    // Hard reload to clear all caches
    window.location.reload()
  }
}

/**
 * Hook to automatically check for new versions and reload
 */
export const useVersionCheck = () => {
  const initialBuildTimeRef = useRef<string | null>(null)

  useEffect(() => {
    // Get the build time when the app first loads
    const currentBuildTime = getCurrentBuildTime()

    if (!currentBuildTime || currentBuildTime === 'BUILD_TIME_PLACEHOLDER') {
      // Development mode or build time not set
      return
    }

    // Store initial build time (only first time in this session)
    if (!initialBuildTimeRef.current) {
      // Always use the current build time from the loaded page
      // This ensures we track the version the user actually has running
      initialBuildTimeRef.current = currentBuildTime

      // Update localStorage to match current version
      localStorage.setItem(INITIAL_BUILD_TIME_KEY, currentBuildTime)
    }

    // Set up periodic version checking
    const intervalId = setInterval(() => {
      checkForNewVersion(initialBuildTimeRef.current!)
    }, CHECK_INTERVAL)

    // Also check immediately after 30 seconds (catch quick deployments)
    const timeoutId = setTimeout(() => {
      checkForNewVersion(initialBuildTimeRef.current!)
    }, 30000)

    return () => {
      clearInterval(intervalId)
      clearTimeout(timeoutId)
    }
  }, [])
}
